export type BlogDescriptionSegment =
  | { type: "html"; content: string; key: string }
  | {
      type: "brightcove";
      brightcoveVideoId: string;
      videoUrl: string;
      thumbnail: string;
      key: string;
    };

/** Extracts an attribute value from an HTML opening-tag string. */
const extractAttr = (tag: string, attr: string): string => {
  const match = new RegExp(`${attr}="([^"]*)"`).exec(tag);
  return match ? match[1] : "";
};

/**
 * Splits a blog description HTML string into plain-HTML and Brightcove embed segments.
 *
 * Sentinel format written by assetDescMake() in TimeLineBlogs.tsx:
 *   <div class='removeContentDescription'>
 *     <div class="brightcove-embed descAsset" data-brightcove-id="<ID>"
 *          data-thumbnail="<POSTER>" data-video-url="<DIRECT_URL>"></div>
 *   </div>
 *
 * The brightcove segment carries `videoUrl` (the direct file URL) and `thumbnail`
 * so the authoring side can attempt the HTML5 player and fall back to the
 * thumbnail when the file isn't browser-accessible; the User-Experience side
 * renders the Brightcove player from the same `data-brightcove-id`.
 *
 * The regex is instantiated inside the function (not as a module-level constant)
 * to avoid shared `lastIndex` state across concurrent calls.
 *
 * Note: the `g` flag is required for exec()-based iteration; `i` is intentionally
 * omitted because HTML class names and data attributes are case-sensitive.
 */
export const parseBlogDescriptionSegments = (html: string): BlogDescriptionSegment[] => {
  if (!html || typeof html !== "string") {
    return [{ type: "html", content: "", key: "html-0" }];
  }
  if (!html.includes("brightcove-embed")) {
    return [{ type: "html", content: html, key: "html-0" }];
  }

  // Matches the removeContentDescription wrapper + inner brightcove sentinel,
  // capturing the inner <div ...> opening tag so its data-* attributes can be read.
  const BC_SENTINEL_REGEX =
    /<div[^>]*class=['"]removeContentDescription['"][^>]*>\s*(<div[^>]*class="brightcove-embed[^"]*"[^>]*>)\s*<\/div>\s*<\/div>/g;

  const result: BlogDescriptionSegment[] = [];
  let lastIndex = 0;
  let htmlIdx = 0;
  let bcIdx = 0;
  let match: RegExpExecArray | null;

  while ((match = BC_SENTINEL_REGEX.exec(html)) !== null) {
    const [fullMatch, innerTag] = match;
    const brightcoveVideoId = extractAttr(innerTag, "data-brightcove-id");
    if (!brightcoveVideoId) continue;
    const matchStart = match.index;
    if (matchStart > lastIndex) {
      result.push({
        type: "html",
        content: html.slice(lastIndex, matchStart),
        key: `html-${htmlIdx++}`,
      });
    }
    result.push({
      type: "brightcove",
      brightcoveVideoId,
      videoUrl: extractAttr(innerTag, "data-video-url"),
      thumbnail: extractAttr(innerTag, "data-thumbnail"),
      key: `bc-${bcIdx++}`,
    });
    lastIndex = matchStart + fullMatch.length;
  }

  if (lastIndex < html.length) {
    result.push({ type: "html", content: html.slice(lastIndex), key: `html-${htmlIdx++}` });
  }

  return result.length > 0 ? result : [{ type: "html", content: html, key: "html-0" }];
};
